import { apiInstance, gql } from "@/services/httpAdapter";

// Commons Fragments
import {
	BonusDefinitionFragment,
	CallActivityPerformanceFragment,
	CommissionTiersFragment,
	CustomerSatisfactionStoreFragment,
	LongevityFragment,
	MetricFragment,
	InputValueFragment,
} from "@PayPlans/api/fragments/commons";

// Additional Fragments
import { GrossDepartmentalFragment, OperatingCostsFragment } from "@PayPlans/api/fragments/managers/departmental";
import { SFEBonusFragment } from "@PayPlans/api/fragments/managers/sales";

// Queries
export async function getServiceDepartmentalPayPlans(locationId: string, position: string) {
	const query = gql`
		query getServiceDepartmentalPayPlans($locationId: String!, $position: String!) {
			getServiceDepartmentalPayPlans(locationId: $locationId, position: $position) {
				_id
				locationId
				name
				position
				bodyShop {
					...GrossDepartmental
				}
				parts {
					...GrossDepartmental
				}
				service {
					...GrossDepartmental
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				ebe {
					...SFEBonus
				}
				pase {
					...SFEBonus
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${BonusDefinitionFragment}
		${GrossDepartmentalFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionStoreFragment}
		${MetricFragment}
		${SFEBonusFragment}
		${LongevityFragment}
		${OperatingCostsFragment}
		${InputValueFragment}
	`;

	const variables = {
		locationId,
		position,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getServiceDepartmentalPayPlans || [];
	} catch (err) {
		console.error(err);
		return null;
	}
}

export async function getServiceDepartmentalPayPlan(locationId: string, payPlanId: string, position: string) {
	const query = gql`
		query getServiceDepartmentalPayPlan($locationId: String!, $id: String!, $position: String!) {
			getServiceDepartmentalPayPlan(locationId: $locationId, id: $id, position: $position) {
				_id
				locationId
				name
				position
				bodyShop {
					...GrossDepartmental
				}
				parts {
					...GrossDepartmental
				}
				service {
					...GrossDepartmental
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				customerSatisfaction {
					...CustomerSatisfactionStore
				}
				ebe {
					...SFEBonus
				}
				pase {
					...SFEBonus
				}
				longevity {
					...Longevity
				}
				operatingCosts {
					...OperatingCosts
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					_id
					fullName
				}
				updatedAt
				updatedBy {
					_id
					fullName
				}
			}
		}
		${BonusDefinitionFragment}
		${GrossDepartmentalFragment}
		${CallActivityPerformanceFragment}
		${CommissionTiersFragment}
		${CustomerSatisfactionStoreFragment}
		${MetricFragment}
		${SFEBonusFragment}
		${LongevityFragment}
		${OperatingCostsFragment}
		${InputValueFragment}
	`;

	const variables = {
		locationId,
		id: payPlanId,
		position,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getServiceDepartmentalPayPlan || [];
	} catch (err) {
		console.error(err);
		return null;
	}
}

export async function getServiceDepartmentalPayPlanMatches(locationId: string, payPlanId: string, position: string) {
	const query = gql`
		query getServiceDepartmentalPayPlanMatches($locationId: String!, $id: String!, $position: String!) {
			getServiceDepartmentalPayPlanMatches(id: $id, locationId: $locationId, position: $position) {
				locationId
				payPlanId
				fullMatchList
				partialMatchList
			}
		}
	`;

	const variables = {
		locationId,
		position,
		id: payPlanId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getServiceDepartmentalPayPlanMatches;
	} catch (err) {
		console.error(err);
	}

	return {
		locationId,
		payPlanId,
		fullMatchList: [],
		partialMatchList: [],
	};
}

export async function getServiceDepartmentalPayPlansSidebarTree(locationId: string, position: string, dontGetAdditionalPositions = false) {
	const query = gql`
		query getServiceDepartmentalPayPlansSidebarTree($locationId: String!, $position: String!, $dontGetAdditionalPositions: Boolean) {
			getServiceDepartmentalPayPlansSidebarTree(locationId: $locationId, position: $position, dontGetAdditionalPositions: $dontGetAdditionalPositions) {
				defaultPayPlans {
					defaultPayPlan {
						_id
						name
						position
						pdfAvailability
					}
					newVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
					preOwnedVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
				}
				available {
					_id
					name
					position
					pdfAvailability
				}
				inProgress {
					_id
					name
					position
					pdfAvailability
				}
				currentlyAssigned {
					_id
					name
					position
					pdfAvailability
				}
				previouslyAssigned {
					_id
					name
					position
					pdfAvailability
				}
			}
		}
	`;

	const variables = {
		locationId,
		position,
		dontGetAdditionalPositions
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getServiceDepartmentalPayPlansSidebarTree || [];
	} catch (err) {
		console.error(err);
		return null;
	}
}
